import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { IPayrollSheetApprovalAccessKey } from "@/payroll/payroll.d";
import { ApprovalToggle } from "./approval-toggle";
import { APPROVAL_GATES } from "./approval-types";
import { GroupApprovalProgress } from "./group-approval-progress";
import type { ApprovalPanelProps } from "./types";
import {
  describeApprovalProvenance,
  getSheetSummary,
  getSheetApprovalSubject,
} from "./utils";

/**
 * Sign-off for one unit — what the workbook toolbar opens. With a single unit
 * there is room to spell each gate out, so the two decisions get their names and
 * a line explaining what they mean.
 */
export const SheetApprovalPanel = ({
  accessKey,
  sheetId,
  pendingApproval,
  onApprovalChange,
}: ApprovalPanelProps & {
  accessKey: IPayrollSheetApprovalAccessKey;
  sheetId?: string;
}) => {
  const subject = getSheetApprovalSubject(accessKey, sheetId);
  if (!subject)
    return (
      <Text variant="caption" className="p-4">
        Approval state is updating. Please try again.
      </Text>
    );
  const sheet = "identity" in subject ? subject : undefined;
  const sheetSummary = sheet ? accessKey.name : getSheetSummary(accessKey);

  return (
    <Stack gap={0} align="stretch" className="w-full min-w-0">
      <Stack
        gap={1}
        align="stretch"
        className="w-full min-w-0 border-b border-line px-4 py-3"
      >
        <Text variant="label" className="truncate" title={subject.name}>
          {subject.name}
        </Text>
        {sheetSummary && (
          <Text
            variant="caption"
            tone="muted"
            className="truncate"
            title={sheetSummary}
          >
            {sheetSummary}
          </Text>
        )}
      </Stack>

      {APPROVAL_GATES.map((gate) => (
        <Stack
          key={gate.type}
          align="stretch"
          gap={2}
          className="w-full border-b border-line px-4 py-3 last:border-b-0"
        >
          <Stack
            className="w-full"
            direction="row"
            align="center"
            justify="between"
            gap={4}
          >
            <Stack align="start" gap={0} className="min-w-0">
              <Text variant="caption" className="font-semibold">
                {gate.label}
              </Text>
              <Text variant="caption" tone="muted">
                {describeApprovalProvenance(subject.approvals[gate.type]) ??
                  gate.description}
              </Text>
            </Stack>
            <ApprovalToggle
              gate={gate}
              subject={subject}
              approvalAuthorized={accessKey.approval_authorized}
              isBusy={Boolean(pendingApproval)}
              isPending={
                pendingApproval?.accessKeyId === accessKey.id &&
                pendingApproval.sheetId === sheet?.id &&
                pendingApproval.approvalType === gate.type
              }
              onChange={
                onApprovalChange
                  ? (enabled) =>
                      onApprovalChange(accessKey, gate.type, enabled, sheet)
                  : undefined
              }
            />
          </Stack>
          {sheet && (
            <GroupApprovalProgress
              accessKey={accessKey}
              approvalType={gate.type}
              variant="bar"
            />
          )}
        </Stack>
      ))}

      {!accessKey.approval_authorized && (
        <Text
          variant="caption"
          tone="muted"
          className="border-t border-line px-4 py-2.5"
        >
          You can see these sheets, but you can&apos;t approve them.
        </Text>
      )}
    </Stack>
  );
};
